import React, { useState } from "react";
import MasjidPicker from "./MasjidPicker";
import AudioChoicePicker from "./AudioChoicePicker";
import AzanOptions from "./AzanOptions";
import BrandMark from "./BrandMark";
import LicenceForm from "./LicenceForm";
import { HomeLicenceState } from "./homeLicence";
import { toHomeMasjid } from "./masjidIndex";
import {
  AudioChoice,
  AzanSettings,
  HomeMasjid,
  HomeSettings,
  MAX_FAVOURITES,
  suggestedAzan,
} from "./homeSettings";

interface SetupWizardProps {
  licence: HomeLicenceState;
  onDone: (settings: HomeSettings) => void;
}

type Step = "code" | "home" | "sound" | "favourites";

// First run: the code (or the preview), the home masjid, its sound (live audio and azan), then up
// to three more favourites. The preview stops after the home masjid.
const SetupWizard: React.FC<SetupWizardProps> = ({ licence, onDone }) => {
  // Asked once: "Start again" on a tablet with a code goes straight to the masjid.
  const [askCode] = useState(!licence.licensed);
  const [step, setStep] = useState<Step>(askCode ? "code" : "home");
  const [favourites, setFavourites] = useState<HomeMasjid[]>([]);
  const [audio, setAudio] = useState<Record<string, AudioChoice>>({});
  const [azan, setAzan] = useState<AzanSettings | null>(null);
  const home = favourites[0];
  const azanShown = azan ?? suggestedAzan({ favourites, audio });

  const steps: Step[] = [
    ...(askCode ? ["code" as const] : []),
    "home",
    ...(licence.licensed ? ["sound" as const, "favourites" as const] : []),
  ];

  const finish = () => onDone({ favourites, audio, azan: azanShown });

  return (
    <div className="hs-sheet hs-setup">
      <header className="hs-sheet-head">
        <BrandMark className="hs-logo" />
        <div>
          <div className="hs-eyebrow">Masjid 24/7 Home</div>
          <h1>
            {step === "code" && "Your code"}
            {step === "home" && "Which is your masjid?"}
            {step === "sound" && "Sound"}
            {step === "favourites" && "Any other masjids?"}
          </h1>
        </div>
        <span className="hs-steps">
          Step {steps.indexOf(step) + 1} of {steps.length}
        </span>
      </header>

      <div className="hs-sheet-body">
        {step === "code" && (
          <>
            <p className="hs-lede">
              Enter your Masjid 24/7 Home code. One code works on one tablet.
            </p>
            <LicenceForm activate={licence.activate} onActivated={() => setStep("home")} />
            <h2>Try it first</h2>
            <p className="hs-lede">
              The preview shows your masjid's prayer times on this tablet. The azan, posters, live
              audio that starts by itself and more masjids come with a code.
            </p>
            <button className="hs-button" onClick={() => setStep("home")}>
              Try the preview
            </button>
          </>
        )}

        {step === "home" && (
          <>
            <p className="hs-lede">
              {licence.licensed
                ? "This tablet will show its prayer times and notices. You can add others later."
                : "This tablet will show its prayer times."}
            </p>
            <MasjidPicker
              autoFocus
              onPick={(masjid) => {
                const chosen = [toHomeMasjid(masjid)];
                setFavourites(chosen);
                if (licence.licensed) setStep("sound");
                // Ready for when a code is entered: the azan as setup would suggest it.
                else
                  onDone({
                    favourites: chosen,
                    audio: {},
                    azan: suggestedAzan({ favourites: chosen, audio: {} }),
                  });
              }}
            />
            {askCode && (
              <div className="hs-actions">
                <button className="hs-button" onClick={() => setStep("code")}>
                  Back
                </button>
              </div>
            )}
          </>
        )}

        {step === "sound" && (
          <>
            {home.audio && (
              <>
                <h2>When {home.name} goes live</h2>
                <p className="hs-lede">It broadcasts live audio. Choose what this tablet does when it starts.</p>
                <AudioChoicePicker
                  masjid={home}
                  isHome
                  value={audio[home.id]}
                  onChange={(choice) => setAudio({ ...audio, [home.id]: choice })}
                />
              </>
            )}
            <h2>Azan at home</h2>
            <p className="hs-lede">An azan on this tablet at {home.name}'s prayer times.</p>
            <AzanOptions azan={azanShown} jamaatWord="Jama'ah" onChange={setAzan} />
            <div className="hs-actions">
              <button className="hs-button" onClick={() => setStep("home")}>
                Back
              </button>
              <button className="hs-button hs-primary" onClick={() => setStep("favourites")}>
                Next
              </button>
            </div>
          </>
        )}

        {step === "favourites" && (
          <>
            <p className="hs-lede">
              Add up to {MAX_FAVOURITES - 1} more, for example family's or work masjids. Swipe
              across the screen to move between them.
            </p>
            <ol className="hs-chosen">
              {favourites.map((masjid, i) => (
                <li key={masjid.id}>
                  <span>
                    <strong>{masjid.name}</strong>
                    {i === 0 ? " · home" : masjid.town ? ` · ${masjid.town}` : ""}
                  </span>
                  {i > 0 && (
                    <button
                      className="hs-link"
                      onClick={() => setFavourites(favourites.filter((m) => m.id !== masjid.id))}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ol>
            {favourites.length < MAX_FAVOURITES && (
              <MasjidPicker
                added={favourites.map((m) => m.id)}
                onPick={(masjid) => setFavourites([...favourites, toHomeMasjid(masjid)])}
              />
            )}
            <div className="hs-actions">
              <button
                className="hs-button"
                onClick={() => setStep("sound")}
              >
                Back
              </button>
              <button className="hs-button hs-primary" onClick={finish}>
                {favourites.length > 1 ? "Done" : "Skip and finish"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SetupWizard;
